/**
 * Helpers partagés — thème DONNÉES 3ème (attendus 213-215, d14 O1-O3).
 *
 * NEUF : svgHisto — histogramme à classes de même amplitude (barres ACCOLÉES,
 * axe x numérique aux bornes des classes), AUTO-DESCRIPTIF : un bloc <desc>
 * « histo|lo0|amplitude|v1,v2,… » est relu par le solveur (patron <desc> des
 * chantiers 4e — jamais de lecture pixel).
 *
 * Statistiques exactes en entiers : moyenne re-dérivable (somme, effectif),
 * médiane par rang, étendue.
 */

export const COULEUR = '#1E3A8A';

/**
 * svgHisto(lo0, amplitude, valeurs, opts) → string SVG.
 * Classes [lo0 ; lo0+amp[, [lo0+amp ; lo0+2amp[, … — barres accolées.
 * opts : { yStep, yLabel, xLabel, compact, manquantIdx } —
 * manquantIdx : barre dessinée en pointillés avec « ? » (effectif inconnu).
 */
export const svgHisto = (lo0, amplitude, valeurs, opts = {}) => {
    const { yStep = 10, yLabel = 'Effectifs', xLabel = '', compact = false, manquantIdx = -1 } = opts;
    const n = valeurs.length;
    const yMax = Math.ceil(Math.max(...valeurs, 1) / yStep) * yStep;
    const colW = compact ? 36 : 52;
    const mL = compact ? 34 : 46;
    const mT = 26;                                         // dégage le libellé « Effectifs » du 1er nombre
    const plotH = compact ? 120 : 180;
    const fs = compact ? 10 : 12;
    const mB = xLabel ? 44 : 30;
    const W = mL + n * colW + 20;
    const H = mT + plotH + mB;
    const yPix = (v) => mT + plotH - (v / yMax) * plotH;

    const nGrad = Math.round(yMax / yStep);
    const labelEvery = nGrad > 12 ? 2 : 1;
    let grille = '';
    for (let i = 0; i <= nGrad; i++) {
        const y = yPix(i * yStep).toFixed(1);
        grille += `<line x1="${mL}" y1="${y}" x2="${W - 12}" y2="${y}" stroke="#cbd5e1" stroke-width="1" stroke-dasharray="3,3"/>`;
        if (i % labelEvery === 0) grille += `<text x="${mL - 5}" y="${+y + 4}" font-size="${fs}" fill="#334155" text-anchor="end">${i * yStep}</text>`;
    }

    let barres = '';
    valeurs.forEach((v, i) => {
        const x = mL + i * colW;
        const h = (Math.min(v, yMax) / yMax) * plotH;
        if (i === manquantIdx) {
            barres += `<rect x="${x}" y="${mT}" width="${colW}" height="${plotH}" fill="none" stroke="#94a3b8" stroke-width="1.5" stroke-dasharray="5,4"/>`
                + `<text x="${x + colW / 2}" y="${mT + plotH / 2}" font-size="${fs + 4}" fill="#64748b" text-anchor="middle">?</text>`;
        } else if (h > 0) {
            barres += `<rect x="${x}" y="${(mT + plotH - h).toFixed(1)}" width="${colW}" height="${h.toFixed(1)}" fill="${COULEUR}" fill-opacity="0.72" stroke="${COULEUR}" stroke-width="1.5"/>`;
        }
        // Borne gauche de chaque classe, puis la borne droite finale.
        barres += `<text x="${x}" y="${mT + plotH + fs + 4}" font-size="${fs}" fill="#1e293b" text-anchor="middle">${lo0 + i * amplitude}</text>`;
    });
    barres += `<text x="${mL + n * colW}" y="${mT + plotH + fs + 4}" font-size="${fs}" fill="#1e293b" text-anchor="middle">${lo0 + n * amplitude}</text>`;

    const axes = `<line x1="${mL}" y1="${mT}" x2="${mL}" y2="${mT + plotH}" stroke="#334155" stroke-width="1.5"/>`
        + `<line x1="${mL}" y1="${mT + plotH}" x2="${W - 12}" y2="${mT + plotH}" stroke="#334155" stroke-width="1.5"/>`;
    const labels = `<text x="10" y="12" font-size="${fs}" fill="#334155">${yLabel}</text>`
        + (xLabel ? `<text x="${mL + (W - mL) / 2}" y="${H - 6}" font-size="${fs}" fill="#334155" text-anchor="middle">${xLabel}</text>` : '');
    const desc = `<desc>histo|${lo0}|${amplitude}|${valeurs.join(',')}</desc>`;

    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="${W}" height="${H}" font-family="Arial">${desc}`
        + `<rect x="0" y="0" width="${W}" height="${H}" fill="#f8fafc"/>${grille}${axes}${barres}${labels}</svg>`;
};

/** Somme d'un tableau d'entiers. */
export const somme = (t) => t.reduce((a, b) => a + b, 0);

/** Médiane par rang sur (valeurs, effectifs) triés par valeur — null si non entière/demi. */
export const medianeTableau = (valeurs, effectifs) => {
    const N = somme(effectifs);
    const rang = (r) => {                                  // r-ième valeur (1-indexé)
        let cumul = 0;
        for (let i = 0; i < valeurs.length; i++) { cumul += effectifs[i]; if (r <= cumul) return valeurs[i]; }
        return valeurs[valeurs.length - 1];
    };
    if (N % 2 === 1) return rang((N + 1) / 2);
    return (rang(N / 2) + rang(N / 2 + 1)) / 2;
};
